/**
 * useAsyncAction Hook
 * Standardized async action handler with loading state and error handling
 *
 * Replaces repetitive try-catch-toast patterns across the codebase.
 *
 * @example
 * const { execute, isLoading } = useAsyncAction<Contact>();
 *
 * const handleBan = () => execute(
 *     () => contactsApi.banContact(contact.id),
 *     {
 *         successMessage: 'Contact banned',
 *         errorAction: 'ban contact',
 *         onSuccess: (updated) => onUpdate?.(updated)
 *     }
 * );
 */

import { useState, useCallback, useRef } from 'react';
import { handleApiError, showSuccess } from '@/utils/errorHandler';

export interface AsyncActionOptions<T> {
    /** Callback executed on successful action */
    onSuccess?: (result: T) => void;
    /** Callback executed on error */
    onError?: (error: unknown) => void;
    /** Success toast message */
    successMessage?: string;
    /** Action description for error message (e.g., 'ban contact') */
    errorAction?: string;
    /** Custom error message (overrides errorAction) */
    errorMessage?: string;
    /** Whether to show error toast (default: true) */
    showErrorToast?: boolean;
}

export interface UseAsyncActionReturn<T> {
    /** Execute the async action */
    execute: (
        action: () => Promise<T>,
        options?: AsyncActionOptions<T>
    ) => Promise<T | null>;
    /** Current loading state */
    isLoading: boolean;
    /** Last error that occurred */
    error: string | null;
    /** Reset error state */
    clearError: () => void;
}

/**
 * Hook for handling async actions with consistent loading, error, and success patterns
 *
 * @returns Object with execute function, loading state, and error handling
 */
export function useAsyncAction<T = void>(): UseAsyncActionReturn<T> {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const mountedRef = useRef(true);

    // Track mounted state for cleanup
    useState(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    });

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const execute = useCallback(async (
        action: () => Promise<T>,
        options: AsyncActionOptions<T> = {}
    ): Promise<T | null> => {
        const {
            onSuccess,
            onError,
            successMessage,
            errorAction = 'perform action',
            errorMessage,
            showErrorToast = true,
        } = options;

        try {
            setIsLoading(true);
            setError(null);

            const result = await action();

            if (mountedRef.current) {
                if (successMessage) {
                    showSuccess(successMessage);
                }
                onSuccess?.(result);
            }

            return result;
        } catch (err) {
            if (mountedRef.current) {
                const message = errorMessage || handleApiError(
                    err,
                    errorAction,
                    { showToast: showErrorToast }
                );
                setError(message);
                onError?.(err);
            }
            return null;
        } finally {
            if (mountedRef.current) {
                setIsLoading(false);
            }
        }
    }, []);

    return {
        execute,
        isLoading,
        error,
        clearError,
    };
}

/**
 * Variant that tracks multiple concurrent actions by ID
 * Useful for list items where each can be loading independently
 *
 * @example
 * const { execute, isLoadingId } = useAsyncActionById<Contact>();
 *
 * const handleBan = (id: number) => execute(
 *     id,
 *     () => contactsApi.banContact(id),
 *     { successMessage: 'Contact banned' }
 * );
 *
 * // In render:
 * <button disabled={isLoadingId(contact.id)}>Ban</button>
 */
export function useAsyncActionById<T = void>() {
    const [loadingIds, setLoadingIds] = useState<Set<string | number>>(new Set());
    const [errors, setErrors] = useState<Map<string | number, string>>(new Map());
    const mountedRef = useRef(true);

    const isLoadingId = useCallback((id: string | number) => {
        return loadingIds.has(id);
    }, [loadingIds]);

    const getError = useCallback((id: string | number) => {
        return errors.get(id) || null;
    }, [errors]);

    const clearError = useCallback((id: string | number) => {
        setErrors(prev => {
            const next = new Map(prev);
            next.delete(id);
            return next;
        });
    }, []);

    const execute = useCallback(async (
        id: string | number,
        action: () => Promise<T>,
        options: AsyncActionOptions<T> = {}
    ): Promise<T | null> => {
        const {
            onSuccess,
            onError,
            successMessage,
            errorAction = 'perform action',
            errorMessage,
            showErrorToast = true,
        } = options;

        try {
            setLoadingIds(prev => new Set(prev).add(id));
            setErrors(prev => {
                const next = new Map(prev);
                next.delete(id);
                return next;
            });

            const result = await action();

            if (mountedRef.current) {
                if (successMessage) {
                    showSuccess(successMessage);
                }
                onSuccess?.(result);
            }

            return result;
        } catch (err) {
            if (mountedRef.current) {
                const message = errorMessage || handleApiError(
                    err,
                    errorAction,
                    { showToast: showErrorToast }
                );
                setErrors(prev => new Map(prev).set(id, message));
                onError?.(err);
            }
            return null;
        } finally {
            if (mountedRef.current) {
                setLoadingIds(prev => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
            }
        }
    }, []);

    return {
        execute,
        isLoadingId,
        isLoading: loadingIds.size > 0,
        getError,
        clearError,
    };
}

export default useAsyncAction;
